import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsDefaultMaterial,
  HsGraphicCollection,
  HsGraphicPoints,
  HsGraphicSprite,
  HsMatrix4,
  HsQuaternion,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  HsSpriteConstants,
  HsTextureMaterial,
  HsTextureUtils,
  hsGenerateCubeGeometryData,
  hsIndexGeometry,
  hsMakeGeometry,
  hsMakeIndexedGeometry,
  type HsDefaultShader,
  type HsGeometry,
  type HsGraphics,
  type HsHypersceneTypes,
  type HsHypersceneViewer,
  type HsMaterial,
  type HsRegularGeometry,
  type HsSpriteGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeSpritePointsMaterial, spritePointSize } from './sprite-points-material.function';

const CAMERA_MATRIX: HsMatrix4 = HsMatrix4.compose(
  [-43, 24, 42],
  HsQuaternion.makeFromAxisAndAngle([-0.38, -0.9, -0.17], 0.9).values,
  [1, 1, 1]
);
const SPRITE_MIN_Y_POSITION: number = 2;
const SPRITE_MAX_Y_POSITION: number = 30;
const SPRITE_FALL_STEP: number = 0.01;
/** Half the width of a sprite in world units. */
const SPRITE_SCALE: number = 0.4;
const GROUND_ITEMS_SIZE: number = 40;
const UNIQUE_SPRITES_COUNT: number = 5000;
const COLLECTION_SPRITES_COUNT: number = 200000;

const IMAGE_URLS: string[] = [
  '/assets/img/snowflake-1.png',
  '/assets/img/snowflake-2.png',
  '/assets/img/snowflake-3.png',
  '/assets/img/snowflake-4.png',
  '/assets/img/snowflake-5.png',
];

enum ObjectType {
  Unique = 'Unique',
  Collection = 'Collection',
}

function getRandomNumber(min: number, max: number): number {
  return Math.random() * (max - min + 1) + min;
}

/** A checkerboard of instanced cubes. */
function makeCheckerGround(
  graphics: HsGraphics,
  light: HsHypersceneTypes.Array3,
  dark: HsHypersceneTypes.Array3,
  height: number
): HsGraphicCollection | null {
  const geometry: HsGeometry | null = hsMakeIndexedGeometry(graphics, hsIndexGeometry(hsGenerateCubeGeometryData()));
  if (geometry === null) {
    return null;
  }
  const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
  shaderData.addColor().addDepth().addNormals().addDiffuseLighting();
  const material: HsMaterial = new HsDefaultMaterial(graphics.gl, shaderData.makeShader());
  const ground: HsGraphicCollection = new HsGraphicCollection(getUuid(), 'ground', geometry, material);
  for (let i: number = 0; i < GROUND_ITEMS_SIZE; i++) {
    for (let j: number = 0; j < GROUND_ITEMS_SIZE; j++) {
      const matrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix().setPosition([
        i * 2 - GROUND_ITEMS_SIZE,
        height,
        j * 2 - GROUND_ITEMS_SIZE,
      ]);
      ground.addInstance(matrix.values, (i + j) % 2 === 1 ? dark : light);
    }
  }
  ground.updateCenter();
  ground.updateBoundingSphere();
  ground.updateBuffers(graphics);
  return ground;
}

/**
 * Falling snowflake sprites over a checkerboard: thousands of separate sprite objects with random textures, or
 * hundreds of thousands drawn at once with a single texture.
 */
@Component({
  selector: 'hyperscene-sprites',
  templateUrl: './sprites.component.html',
  styleUrls: ['./sprites.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class SpritesDemo extends DemoViewerPage {
  public readonly objectTypes: string[] = [ObjectType.Unique, ObjectType.Collection];

  public selectedObjectType: ObjectType = ObjectType.Unique;

  private readonly uniqueSprites: HsGraphicSprite[] = [];
  private spriteCollection: HsGraphicPoints | null = null;

  protected async initScene(viewer: HsHypersceneViewer): Promise<void> {
    const ground: HsGraphicCollection | null = makeCheckerGround(
      viewer.surface.graphics,
      [0.7, 0.7, 0.7],
      [0.3, 0.3, 0.3],
      0
    );
    if (ground !== null) {
      viewer.add(ground);
    }

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.copy(CAMERA_MATRIX);
    viewer.camera.updateProjectionMatrix();

    const textures: (WebGLTexture | null)[] = await Promise.all(
      IMAGE_URLS.map((url: string) => HsTextureUtils.load(viewer.gl, url))
    );
    if (this.viewer !== viewer || textures.some((texture: WebGLTexture | null) => texture === null)) {
      return;
    }
    this.createUniqueSprites(viewer, textures as WebGLTexture[]);
    this.createSpriteCollection(viewer, textures as WebGLTexture[]);
    this.selectObject(this.selectedObjectType);
  }

  protected override animate(viewer: HsHypersceneViewer): boolean {
    if (this.spriteCollection !== null) {
      this.spriteCollection.setPointSize(
        spritePointSize(SPRITE_SCALE * 2, viewer.gl.drawingBufferHeight, viewer.camera.fov)
      );
    }
    if (this.selectedObjectType !== ObjectType.Unique || this.uniqueSprites.length === 0) {
      return false;
    }
    for (const sprite of this.uniqueSprites) {
      const values: HsHypersceneTypes.Array16 = sprite.worldMatrix.values;
      values[13] = values[13] < 1 ? SPRITE_MAX_Y_POSITION : values[13] - SPRITE_FALL_STEP;
      sprite.updateBoundingSphere();
    }
    return true;
  }

  public selectObject(objectType: ObjectType): void {
    this.selectedObjectType = objectType;
    const viewer: HsHypersceneViewer | null = this.viewer;
    if (viewer === null || this.spriteCollection === null) {
      return;
    }
    if (objectType === ObjectType.Unique) {
      viewer.queue.removeObject(this.spriteCollection);
      viewer.queue.addObjects(this.uniqueSprites);
    } else {
      this.uniqueSprites.forEach((sprite: HsGraphicSprite) => viewer.queue.removeObject(sprite));
      viewer.queue.addObject(this.spriteCollection);
    }
    viewer.invalidate();
  }

  private createUniqueSprites(viewer: HsHypersceneViewer, textures: WebGLTexture[]): void {
    const geometry: HsSpriteGeometry | null = HsSpriteConstants.makeSpriteGeometry(viewer.surface.graphics);
    if (geometry === null) {
      return;
    }
    const shader: HsDefaultShader = new HsShaderData(HsShaderObjectType.single)
      .addColor()
      .addDepth()
      .addTexture()
      .makeShader();
    shader.hasTransparency = true;
    // One material per texture; all of them share the shader compiled by the first.
    const materials: HsTextureMaterial<HsDefaultShader>[] = textures.map((texture: WebGLTexture, index: number) => {
      const material: HsTextureMaterial<HsDefaultShader> = new HsTextureMaterial(viewer.gl, shader, index === 0);
      material.color.set(1, 1, 1);
      material.texture = texture;
      return material;
    });

    for (let k: number = 0; k < UNIQUE_SPRITES_COUNT; k++) {
      const material: HsTextureMaterial<HsDefaultShader> = materials[Math.floor(Math.random() * materials.length)];
      const sprite: HsGraphicSprite = new HsGraphicSprite(getUuid(), 'sprite', geometry, material);
      sprite.scale = SPRITE_SCALE;
      sprite.worldMatrix.setPosition([
        getRandomNumber(-(GROUND_ITEMS_SIZE + 1), GROUND_ITEMS_SIZE - 1),
        getRandomNumber(SPRITE_MIN_Y_POSITION, SPRITE_MAX_Y_POSITION),
        getRandomNumber(-(GROUND_ITEMS_SIZE + 1), GROUND_ITEMS_SIZE - 1),
      ]);
      sprite.updateBoundingSphere();
      this.uniqueSprites.push(sprite);
    }
  }

  private createSpriteCollection(viewer: HsHypersceneViewer, textures: WebGLTexture[]): void {
    const positions: number[] = [];
    for (let k: number = 0; k < COLLECTION_SPRITES_COUNT; k++) {
      positions.push(
        getRandomNumber(-(GROUND_ITEMS_SIZE - 1), GROUND_ITEMS_SIZE - 1),
        getRandomNumber(SPRITE_MIN_Y_POSITION, SPRITE_MAX_Y_POSITION),
        getRandomNumber(-(GROUND_ITEMS_SIZE - 1), GROUND_ITEMS_SIZE - 1)
      );
    }
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      viewer.surface.graphics,
      new HsRegularGeometryData(positions, [])
    );
    if (geometry === null) {
      return;
    }
    const texture: WebGLTexture = textures[Math.floor(Math.random() * textures.length)];
    this.spriteCollection = new HsGraphicPoints(
      getUuid(),
      'spriteCollection',
      geometry,
      makeSpritePointsMaterial(viewer.gl, texture)
    );
    this.spriteCollection.updateBoundingSphere();
  }
}
